import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Code2 } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import toast from 'react-hot-toast';

interface BaseSystem {
  name: string;
  base: number;
  prefix: string;
  description: string;
  validChars: string;
}

// This will be moved inside the component to access t function

export const BaseConverterTool: React.FC = () => {
  const { t } = useTranslation();

  const baseSystems: BaseSystem[] = [
    {
      name: t('tools.baseConverter.binary'),
      base: 2,
      prefix: '0b',
      description: t('tools.baseConverter.binaryDescription'),
      validChars: '01'
    },
    {
      name: t('tools.baseConverter.octal'),
      base: 8,
      prefix: '0o',
      description: t('tools.baseConverter.octalDescription'),
      validChars: '01234567'
    },
    {
      name: t('tools.baseConverter.decimal'),
      base: 10,
      prefix: '',
      description: t('tools.baseConverter.decimalDescription'),
      validChars: '0123456789'
    },
    {
      name: t('tools.baseConverter.hexadecimal'),
      base: 16,
      prefix: '0x',
      description: t('tools.baseConverter.hexadecimalDescription'),
      validChars: '0123456789ABCDEFabcdef'
    }
  ];

  const [inputValue, setInputValue] = useState('255');
  const [inputBase, setInputBase] = useState(baseSystems[2]); // Decimal
  const [conversions, setConversions] = useState<Record<string, string>>({});
  const [error, setError] = useState('');

  const isValidInput = (value: string, baseSystem: BaseSystem): boolean => {
    if (!value) return false;
    
    // Remove prefix if present
    let cleanValue = value;
    if (baseSystem.prefix && value.toLowerCase().startsWith(baseSystem.prefix.toLowerCase())) {
      cleanValue = value.slice(baseSystem.prefix.length);
    }
    
    // Check if all characters are valid for this base
    return cleanValue.split('').every(char => baseSystem.validChars.includes(char));
  };

  const convertToDecimal = (value: string, fromBase: number): number => {
    // Remove prefix if present
    let cleanValue = value;
    const baseSystem = baseSystems.find(b => b.base === fromBase);
    if (baseSystem?.prefix && value.toLowerCase().startsWith(baseSystem.prefix.toLowerCase())) {
      cleanValue = value.slice(baseSystem.prefix.length);
    }
    
    return parseInt(cleanValue, fromBase);
  };

  const convertFromDecimal = (decimal: number, toBase: number, includePrefix: boolean = true): string => {
    const baseSystem = baseSystems.find(b => b.base === toBase);
    const converted = decimal.toString(toBase).toUpperCase();
    
    if (includePrefix && baseSystem?.prefix) {
      return baseSystem.prefix + converted;
    }
    
    return converted;
  };

  const updateConversions = () => {
    if (!inputValue.trim()) {
      setConversions({});
      setError('');
      return;
    }

    if (!isValidInput(inputValue, inputBase)) {
      setError(`Invalid input for ${inputBase.name}. Valid characters: ${inputBase.validChars}`);
      setConversions({});
      return;
    }

    try {
      const decimalValue = convertToDecimal(inputValue, inputBase.base);
      
      if (decimalValue < 0) {
        setError(t('tools.baseConverter.negativeNotSupported'));
        setConversions({});
        return;
      }

      if (!isFinite(decimalValue)) {
        setError(t('tools.baseConverter.numberTooLarge'));
        setConversions({});
        return;
      }

      const newConversions: Record<string, string> = {};
      
      baseSystems.forEach(baseSystem => {
        if (baseSystem.base !== inputBase.base) {
          newConversions[baseSystem.name] = convertFromDecimal(decimalValue, baseSystem.base);
        }
      });

      setConversions(newConversions);
      setError('');
    } catch (err) {
      setError(t('tools.baseConverter.invalidFormat'));
      setConversions({});
    }
  };

  useEffect(() => {
    updateConversions();
  }, [inputValue, inputBase]);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('common.copied'));
    } catch (err) {
      toast.error(t('common.failedToCopy'));
    }
  };

  const loadExample = (example: { value: string; base: BaseSystem }) => {
    setInputValue(example.value);
    setInputBase(example.base);
  };

  const examples = [
    { value: '255', base: baseSystems[2], description: t('tools.baseConverter.examples.rgbValue') },
    { value: '0xFF', base: baseSystems[3], description: t('tools.baseConverter.examples.hex255') },
    { value: '11111111', base: baseSystems[0], description: t('tools.baseConverter.examples.binary255') },
    { value: '377', base: baseSystems[1], description: t('tools.baseConverter.examples.octal255') },
    { value: '1024', base: baseSystems[2], description: t('tools.baseConverter.examples.powerOf2') },
    { value: '0x400', base: baseSystems[3], description: t('tools.baseConverter.examples.hex1024') },
  ];

  return (
    <div className="space-y-6">
      {/* Input */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Code2 className="w-5 h-5" />
            <span>{t('tools.baseConverter.baseConverter')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('tools.baseConverter.inputBase')}
                </label>
                <select
                  value={inputBase.name}
                  onChange={(e) => {
                    const base = baseSystems.find(b => b.name === e.target.value);
                    if (base) setInputBase(base);
                  }}
                  className="input"
                >
                  {baseSystems.map((base) => (
                    <option key={base.name} value={base.name}>
                      {base.name} - {base.description}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Number
                </label>
                <Input
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder={t('tools.baseConverter.enterNumberPlaceholder', { base: inputBase.name.toLowerCase() })}
                  className="font-mono"
                />
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md dark:bg-red-900/20 dark:border-red-800">
                <span className="text-sm text-red-700 dark:text-red-400">{error}</span>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('tools.baseConverter.examples.title')}:
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {examples.map((example, index) => (
                  <Button
                    key={index}
                    onClick={() => loadExample(example)}
                    variant="outline"
                    size="sm"
                    className="text-left justify-start"
                  >
                    <div>
                      <div className="font-mono text-xs">{example.value}</div>
                      <div className="text-xs text-gray-500">{example.description}</div>
                    </div>
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Conversions */}
      {Object.keys(conversions).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('tools.baseConverter.conversions')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {baseSystems.map((baseSystem) => {
                const conversion = conversions[baseSystem.name];
                if (!conversion) return null;

                return (
                  <div
                    key={baseSystem.name}
                    className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg"
                  >
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          {baseSystem.name}
                        </span>
                        <span className="text-sm text-gray-500">
                          {baseSystem.description}
                        </span>
                      </div>
                      <div className="font-mono text-lg bg-white dark:bg-gray-700 p-2 rounded border">
                        {conversion}
                      </div>
                    </div>
                    <Button
                      onClick={() => copyToClipboard(conversion)}
                      size="sm"
                      variant="outline"
                      className="ml-4"
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Decimal Value */}
      {inputValue && !error && (
        <Card>
          <CardHeader>
            <CardTitle>{t('tools.baseConverter.decimalValue')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center p-6 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <div className="text-3xl font-mono font-bold text-blue-600 dark:text-blue-400">
                {convertToDecimal(inputValue, inputBase.base).toLocaleString()}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                {t('tools.baseConverter.decimalRepresentation')}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Information */}
      <Card>
        <CardHeader>
          <CardTitle>Number Base Systems</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 text-sm text-gray-600 dark:text-gray-400">
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Common Base Systems</h4>
              <div className="space-y-2">
                {baseSystems.map((base) => (
                  <div key={base.name} className="flex justify-between">
                    <span className="font-medium">{base.name} (Base {base.base}):</span>
                    <span>{base.description}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Usage in Programming</h4>
              <ul className="list-disc list-inside space-y-1">
                <li><strong>Binary:</strong> Used in digital systems, bit operations</li>
                <li><strong>Octal:</strong> File permissions in Unix/Linux systems</li>
                <li><strong>Decimal:</strong> Standard human-readable numbers</li>
                <li><strong>Hexadecimal:</strong> Memory addresses, colors, debugging</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Prefixes</h4>
              <p>
                Many programming languages use prefixes to indicate number bases: 
                0b for binary, 0o for octal, and 0x for hexadecimal. This tool supports both with and without prefixes.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
